// Outsource.jsx - With Multiple Procedures and Currency Context Integration
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useSocket } from "../../../../../context/SocketContext";
import { useCurrency } from "../../../../../context/currencyContext";
import { FaFile, FaFilePdf, FaDownload, FaCube, FaUpload, FaReceipt, FaImage, FaPlus, FaTrash } from "react-icons/fa";
import axios from "axios";

// Pricing access hook — hides price values when user is not Admin
// and does not have pricing access enabled.
import usePricingAccess from "../../../../../hooks/usePricingAccess";

const Outsource = ({ showModal, handleClose, caseId, caseNumber }) => {
  // Get currency context
  const { currency, formatAmount } = useCurrency();

  // Pricing access — Admins get true by default via the hook
  const { hasPricingAccess } = usePricingAccess();

  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [procedures, setProcedures] = useState([]);

  // CHANGED: Multiple procedures state
  const [selectedProceduresList, setSelectedProceduresList] = useState([
    { id: Date.now(), procedureId: "", quantity: 1, price: 0, totalPrice: 0 }
  ]);

  const [requiredDate, setRequiredDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // File states
  const [stlFiles, setStlFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [showFileSection, setShowFileSection] = useState(false);

  // Separate received and uploaded files (only for outsource-case page)
  const [receivedStlFiles, setReceivedStlFiles] = useState([]);
  const [uploadedStlFiles, setUploadedStlFiles] = useState([]);
  const [receivedOtherFiles, setReceivedOtherFiles] = useState([]);
  const [uploadedOtherFiles, setUploadedOtherFiles] = useState([]);
  const [receivedImageFiles, setReceivedImageFiles] = useState([]);
  const [uploadedImageFiles, setUploadedImageFiles] = useState([]);

  // Flag to determine if we're on outsource-case page
  const [isOutsourceCasePage, setIsOutsourceCasePage] = useState(false);

  // ✅ FIX: Holds the MongoDB _id of the source OutsourceCase when re-outsourcing
  const [sourceOutsourceCaseId, setSourceOutsourceCaseId] = useState(null);

  // Get socket from context
  const { socket } = useSocket();

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  // ✅ Helper: get auth header (token from localStorage) — used in all fetch/axios calls
  const getAuthHeader = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Get userLabId from localStorage
  const getCurrentUserLabId = () => {
    const labIdFromStorage = localStorage.getItem('userLabId');
    if (labIdFromStorage) {
      console.log('✅ Found userLabId in localStorage:', labIdFromStorage);
      return labIdFromStorage;
    }

    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        if (parsedData.userLabId) {
          console.log('✅ Found userLabId in userData:', parsedData.userLabId);
          return parsedData.userLabId;
        }
        if (parsedData.labId) {
          console.log('✅ Found labId in userData:', parsedData.labId);
          return parsedData.labId;
        }
      }
    } catch (error) {
      console.error('Error parsing userData:', error);
    }

    try {
      const currentTenant = localStorage.getItem('currentTenant');
      if (currentTenant) {
        const parsedTenant = JSON.parse(currentTenant);
        if (parsedTenant.labId) {
          console.log('✅ Found labId in currentTenant:', parsedTenant.labId);
          return parsedTenant.labId;
        }
      }
    } catch (error) {
      console.error('Error parsing currentTenant:', error);
    }

    console.log('❌ No userLabId found in localStorage');
    return null;
  };

  // Get user lab name from localStorage
  const getUserLabName = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        return parsedData.labName || parsedData.email?.split('@')[0] || 'Unknown Lab';
      }
    } catch (error) {
      console.error('Error getting lab name:', error);
    }
    return localStorage.getItem('userName') || 'Unknown Lab';
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = () => {
    return currency?.currencySymbol || '₮';
  };

  // CHANGED: Calculate grand total for all procedures
  const calculateGrandTotal = () => {
    return selectedProceduresList.reduce((sum, proc) => sum + proc.totalPrice, 0);
  };

  const calculateTotalQuantity = () => {
    return selectedProceduresList.reduce((sum, proc) => sum + proc.quantity, 0);
  };

  // CHANGED: Add new procedure row
  const addProcedureRow = () => {
    setSelectedProceduresList(prev => [
      ...prev,
      { id: Date.now(), procedureId: "", quantity: 1, price: 0, totalPrice: 0 }
    ]);
  };

  // CHANGED: Remove procedure row
  const removeProcedureRow = (rowId) => {
    if (selectedProceduresList.length <= 1) {
      setError("At least one procedure is required");
      return;
    }
    setSelectedProceduresList(prev => prev.filter(p => p.id !== rowId));
    setError("");
  };

  // CHANGED: Handle procedure selection for a specific row
  const handleProcedureChangeForRow = (rowId, procedureId) => {
    setSelectedProceduresList(prev =>
      prev.map(proc => {
        if (proc.id === rowId) {
          if (procedureId) {
            const selectedProc = procedures.find(p => p.procedureId === procedureId);
            const price = selectedProc ? selectedProc.outsourcePrice : 0;
            return {
              ...proc,
              procedureId: procedureId,
              price: price,
              totalPrice: price * proc.quantity
            };
          } else {
            return {
              ...proc,
              procedureId: "",
              price: 0,
              totalPrice: 0,
              quantity: 1
            };
          }
        }
        return proc;
      })
    );
  };

  // CHANGED: Handle quantity change for a specific row
  const handleQuantityChangeForRow = (rowId, newQuantity) => {
    let value = parseInt(newQuantity, 10);
    if (isNaN(value) || value < 1) value = 1;
    if (value > 100) value = 100;

    setSelectedProceduresList(prev =>
      prev.map(proc => {
        if (proc.id === rowId) {
          return {
            ...proc,
            quantity: value,
            totalPrice: proc.price * value
          };
        }
        return proc;
      })
    );
  };

  // CHANGED: Check for duplicate procedures
  const hasDuplicateProcedures = () => {
    const procedureIds = selectedProceduresList
      .filter(p => p.procedureId)
      .map(p => p.procedureId);
    return new Set(procedureIds).size !== procedureIds.length;
  };

  // CHANGED: Get already selected procedures for a specific row
  const getSelectedProcedureIds = (currentRowId) => {
    return selectedProceduresList
      .filter(p => p.id !== currentRowId && p.procedureId)
      .map(p => p.procedureId);
  };

  // Function to normalize file paths for display/download
  const getFileUrl = (file) => {
    if (!file) return '';

    if (file.filePath) {
      const cleanPath = file.filePath.replace(/\\/g, '/');
      if (!cleanPath.includes('/') && !cleanPath.includes('\\')) {
        return `${API_BASE_URL}/uploads/${cleanPath}`;
      }
      const filename = cleanPath.split('/').pop();
      return `${API_BASE_URL}/uploads/${filename}`;
    }

    if (file.path) {
      const cleanPath = file.path.replace(/\\/g, '/');
      const filename = cleanPath.split('/').pop();
      return `${API_BASE_URL}/uploads/${filename}`;
    }

    return '';
  };

  // Function to determine file type based on extension
  const determineFileType = (fileName, filePath) => {
    const path = (filePath || fileName || '').toLowerCase();

    if (/\.stl$/i.test(path)) return 'stl';
    if (/\.(png|jpg|jpeg|gif|bmp|webp|svg|tiff?)$/i.test(path)) return 'image';
    if (/\.pdf$/i.test(path)) return 'pdf';
    if (/\.(doc|docx|txt|zip|rar|obj|ply)$/i.test(path)) return 'other';

    return 'other';
  };

  // Function to normalize file object for display
  const normalizeFile = (file, index, source) => {
    const fileName = file.originalName || file.fileName ||
                    (file.filePath ? file.filePath.split(/[\\/]/).pop() :
                    file.path ? file.path.split(/[\\/]/).pop() : `File_${index}`);

    const filePath = file.filePath || file.path || '';

    return {
      ...file,
      _id: file._id || file.fileId || `${source}_file_${index}_${Date.now()}`,
      name: fileName,
      path: filePath,
      size: file.fileSize || file.size || 0,
      date: file.uploadedAt || file.createdAt || file.updatedAt || new Date().toISOString(),
      fileType: file.fileType || determineFileType(fileName, filePath),
      source: source
    };
  };

  // Detect which page we're on
  const detectPageType = () => {
    const currentPath = window.location.pathname;
    const isOutsourceCase = currentPath.includes('/outsource-case/');
    setIsOutsourceCasePage(isOutsourceCase);
    console.log('📍 Current page type:', isOutsourceCase ? 'Outsource Case Page' : 'Case Page');
    return isOutsourceCase;
  };

  // Fetch case files from case API (for case page)
  const fetchCaseFilesFromCaseAPI = async () => {
    if (!caseId) return;

    console.log('📁 Fetching files from Case API for caseId:', caseId);

    try {
      const userLabId = getCurrentUserLabId();

      // ✅ FIX: Added Authorization header
      const response = await axios.get(
        `${API_BASE_URL}/api/cases/${caseId}/files`,
        {
          headers: {
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            ...getAuthHeader()
          }
        }
      );

      if (response.data.success && response.data.data) {
        const files = response.data.data;
        console.log('📁 Files from Case API:', files.length);

        const processedFiles = files.map((file, index) =>
          normalizeFile(file, index, 'case')
        );

        const stlFilesList = processedFiles.filter(file => file.fileType === 'stl');
        const imageFilesList = processedFiles.filter(file => file.fileType === 'image');
        const otherFilesList = processedFiles.filter(file =>
          file.fileType !== 'stl' && file.fileType !== 'image'
        );

        setStlFiles(stlFilesList);
        setImageFiles(imageFilesList);
        setOtherFiles(otherFilesList);

        setReceivedStlFiles([]);
        setUploadedStlFiles([]);
        setReceivedOtherFiles([]);
        setUploadedOtherFiles([]);
        setReceivedImageFiles([]);
        setUploadedImageFiles([]);

        console.log('✅ STL Files:', stlFilesList.length);
        console.log('✅ Image Files:', imageFilesList.length);
        console.log('✅ Other Files:', otherFilesList.length);
      }
    } catch (error) {
      console.error('❌ Error fetching case files from Case API:', error);
      resetFileStates();
    }
  };

  // Fetch files from outsource case API (for outsource-case page)
  // ✅ FIX: Also captures the MongoDB _id of the source OutsourceCase
  const fetchCaseFilesFromOutsourceAPI = async () => {
    if (!caseNumber) return;

    console.log('📁 Fetching files from Outsource Case API for caseNumber:', caseNumber);

    try {
      const userLabId = getCurrentUserLabId();
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/case-number/${caseNumber}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId
          },
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        const caseData = response.data.data;
        console.log('📊 Full outsource case data:', caseData);

        // ✅ FIX: Capture the source outsource case _id for re-outsourcing
        if (caseData._id) {
          setSourceOutsourceCaseId(caseData._id);
          console.log('🔗 Captured source OutsourceCase _id for re-outsource:', caseData._id);
        }

        const selectedFiles = caseData.selectedFiles || [];
        const uploadedFiles = caseData.uploadedFiles || [];

        console.log('📁 Received (Selected) files count:', selectedFiles.length);
        console.log('📁 Uploaded files count:', uploadedFiles.length);

        const receivedFiles = selectedFiles.map((file, index) =>
          normalizeFile(file, index, 'received')
        );

        const uploadedFilesProcessed = uploadedFiles.map((file, index) =>
          normalizeFile(file, index, 'uploaded')
        );

        const receivedStl = receivedFiles.filter(file => file.fileType === 'stl');
        const receivedImages = receivedFiles.filter(file => file.fileType === 'image');
        const receivedOther = receivedFiles.filter(file =>
          file.fileType !== 'stl' && file.fileType !== 'image'
        );

        const uploadedStl = uploadedFilesProcessed.filter(file => file.fileType === 'stl');
        const uploadedImages = uploadedFilesProcessed.filter(file => file.fileType === 'image');
        const uploadedOther = uploadedFilesProcessed.filter(file =>
          file.fileType !== 'stl' && file.fileType !== 'image'
        );

        setReceivedStlFiles(receivedStl);
        setUploadedStlFiles(uploadedStl);
        setReceivedOtherFiles(receivedOther);
        setUploadedOtherFiles(uploadedOther);
        setReceivedImageFiles(receivedImages);
        setUploadedImageFiles(uploadedImages);

        setStlFiles([...receivedStl, ...uploadedStl]);
        setImageFiles([...receivedImages, ...uploadedImages]);
        setOtherFiles([...receivedOther, ...uploadedOther]);

        console.log('✅ Received STL Files:', receivedStl.length);
        console.log('✅ Received Image Files:', receivedImages.length);
        console.log('✅ Received Other Files:', receivedOther.length);
        console.log('✅ Uploaded STL Files:', uploadedStl.length);
        console.log('✅ Uploaded Image Files:', uploadedImages.length);
        console.log('✅ Uploaded Other Files:', uploadedOther.length);

        return;
      }

      console.log('⚠️ No files in outsource case, falling back to Case API');
      if (caseId) {
        await fetchCaseFilesFromCaseAPI();
      } else {
        resetFileStates();
      }
    } catch (error) {
      console.error('❌ Error fetching files from Outsource Case API:', error);
      if (caseId) {
        console.log('🔄 Falling back to Case API due to error');
        await fetchCaseFilesFromCaseAPI();
      } else {
        resetFileStates();
      }
    }
  };

  const fetchCaseFiles = async () => {
    if (!caseNumber && !caseId) return;

    setLoadingFiles(true);
    console.log('📁 Starting file fetch...');
    console.log('  - caseNumber:', caseNumber);
    console.log('  - caseId:', caseId);

    const isOutsourceCase = detectPageType();

    if (isOutsourceCase) {
      await fetchCaseFilesFromOutsourceAPI();
    } else {
      await fetchCaseFilesFromCaseAPI();
    }

    setLoadingFiles(false);
  };

  const resetFileStates = () => {
    setStlFiles([]);
    setOtherFiles([]);
    setImageFiles([]);
    setReceivedStlFiles([]);
    setUploadedStlFiles([]);
    setReceivedOtherFiles([]);
    setUploadedOtherFiles([]);
    setReceivedImageFiles([]);
    setUploadedImageFiles([]);
  };

  // Fetch providers on modal open
  useEffect(() => {
    if (showModal) {
      fetchProviders();
      fetchCaseFiles();
      // FIXED: Set today's date as default instead of tomorrow
      const today = new Date();
      setRequiredDate(today.toISOString().split('T')[0]);
    }
  }, [showModal, caseId, caseNumber]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showModal) {
      resetForm();
    }
  }, [showModal]);

  const resetForm = () => {
    setSelectedProvider("");
    setProcedures([]);
    setSelectedProceduresList([
      { id: Date.now(), procedureId: "", quantity: 1, price: 0, totalPrice: 0 }
    ]);
    setRequiredDate("");
    setNotes("");
    setError("");
    setSuccess("");
    setIsSubmitting(false);
    setSelectedFileIds([]);
    setShowFileSection(false);
    setIsOutsourceCasePage(false);
    // ✅ FIX: Clear the captured source outsource case id
    setSourceOutsourceCaseId(null);
    resetFileStates();
  };

  // Fetch labs that have shared procedures with current lab
  const fetchProviders = async () => {
    setLoading(true);
    setError("");

    try {
      const currentUserLabId = getCurrentUserLabId();

      console.log('🔍 Current user lab ID from localStorage:', currentUserLabId);

      if (!currentUserLabId) {
        console.error('❌ No userLabId found in localStorage');
        setError("Could not identify your lab. Please try logging in again.");
        setLoading(false);
        return;
      }

      // ✅ FIX: Added Authorization header
      const response = await fetch(`${API_BASE_URL}/api/outsource-case/available-labs`, {
        headers: {
          'Content-Type': 'application/json',
          'userLabId': currentUserLabId,
          ...getAuthHeader()
        }
      });

      const result = await response.json();

      console.log('📊 Available labs response:', result);

      if (response.ok && result.success) {
        const availableLabs = result.data || [];

        if (availableLabs.length === 0) {
          console.log('❌ No available labs found');
          setError("No labs have shared procedures with you yet. Please contact other labs to set up outsourcing arrangements.");
        } else {
          console.log(`✅ Found ${availableLabs.length} available labs for outsourcing:`);
          availableLabs.forEach(lab => {
            console.log(`  - ${lab.labName} (userLabId: ${lab.userLabId}, _id: ${lab._id}) - ${lab.procedureCount} shared procedure(s)`);
          });
          setProviders(availableLabs);
        }
      } else {
        setError(result.message || "Failed to fetch providers");
      }
    } catch (error) {
      console.error('❌ Error fetching providers:', error);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = async (e) => {
    const providerId = e.target.value;
    setSelectedProvider(providerId);
    setSelectedProceduresList([
      { id: Date.now(), procedureId: "", quantity: 1, price: 0, totalPrice: 0 }
    ]);

    if (!providerId) {
      setProcedures([]);
      return;
    }

    setLoadingProcedures(true);
    setError("");

    try {
      const currentUserLabId = getCurrentUserLabId();
      const selectedLab = providers.find(p => p._id === providerId);

      console.log('🔍 Fetching procedures for provider:', selectedLab?.labName, '(MongoDB _id:', providerId, ')');

      // ✅ FIX: Added Authorization header
      const response = await fetch(`${API_BASE_URL}/api/outsource-case/providers/${providerId}/procedures`, {
        headers: {
          'Content-Type': 'application/json',
          'userLabId': currentUserLabId,
          ...getAuthHeader()
        }
      });

      const result = await response.json();

      console.log('📊 Procedures response:', result);

      if (response.ok && result.success) {
        const fetchedProcedures = result.data.procedures || [];

        console.log(`✅ Found ${fetchedProcedures.length} procedures for ${selectedLab?.labName}:`);
        fetchedProcedures.forEach(proc => {
          console.log(`  - ${proc.procedureName} (ID: ${proc.procedureId}) - Price: ${getCurrencySymbol()}${proc.outsourcePrice}`);
        });

        setProcedures(fetchedProcedures);
      } else {
        setError(result.message || "Failed to fetch procedures");
        setProcedures([]);
      }
    } catch (error) {
      console.error('❌ Error fetching procedures:', error);
      setError("Network error. Please try again.");
      setProcedures([]);
    } finally {
      setLoadingProcedures(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (fileId) => {
    setSelectedFileIds(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      } else {
        return [...prev, fileId];
      }
    });
  };

  // Select all files of a type
  const handleSelectAllFiles = (files, select) => {
    const fileIds = files.map(f => f._id);
    if (select) {
      setSelectedFileIds(prev => [...new Set([...prev, ...fileIds])]);
    } else {
      setSelectedFileIds(prev => prev.filter(id => !fileIds.includes(id)));
    }
  };

  // Handle file download with proper error handling
  const handleFileDownload = async (file) => {
    try {
      const fileUrl = getFileUrl(file);
      console.log('Downloading file from:', fileUrl);

      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name || file.originalName || file.fileName || `file_${Date.now()}`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('File download failed:', error);
      setError('Failed to download file');
    }
  };

  const handleSave = async () => {
    if (isSubmitting) {
      console.log('Already submitting, please wait...');
      return;
    }

    if (!selectedProvider) {
      setError("Please select a lab");
      return;
    }

    // CHANGED: Validate at least one valid procedure
    const validProcedures = selectedProceduresList.filter(p => p.procedureId);
    if (validProcedures.length === 0) {
      setError("Please select at least one procedure");
      return;
    }

    // CHANGED: Check for duplicates
    if (hasDuplicateProcedures()) {
      setError("Duplicate procedures detected. Please remove duplicates.");
      return;
    }

    if (!requiredDate) {
      setError("Please select a required date");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const currentUserLabId = getCurrentUserLabId();
      const labName = getUserLabName();
      const selectedProviderData = providers.find(p => p._id === selectedProvider);

      // ✅ FIX: Detect re-outsource and prepare payload
      const isReOutsource = isOutsourceCasePage && !!sourceOutsourceCaseId;

      // CHANGED: Build payload with multiple procedures
      const payload = {
        caseId: caseNumber || caseId,
        caseNumber: caseNumber || caseId,
        destinationLabId: selectedProvider,
        selectedProcedures: validProcedures.map(proc => ({
          procedureId: proc.procedureId,
          quantity: proc.quantity,
          totalPrice: proc.totalPrice
        })),
        requiredDate,
        notes,
        sourceLabId: currentUserLabId,
        selectedFiles: selectedFileIds,
        // ✅ FIX: Tell backend this is a re-outsource and which case to link to
        isReOutsource,
        sourceOutsourceCaseId: isReOutsource ? sourceOutsourceCaseId : null
      };

      const grandTotal = calculateGrandTotal();
      const totalQty = calculateTotalQuantity();

      console.log('📤 Submitting outsource payload:', {
        ...payload,
        selectedFilesCount: selectedFileIds.length,
        sourceLabId: currentUserLabId,
        destinationLabName: selectedProviderData?.labName,
        procedureCount: validProcedures.length,
        grandTotal: grandTotal,
        totalQuantity: totalQty,
        isReOutsource
      });

      // ✅ FIX: Added Authorization header
      const response = await fetch(`${API_BASE_URL}/api/outsource-case/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'userLabId': currentUserLabId,
          ...getAuthHeader()
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      console.log('📥 Create outsource response:', result);

      if (response.ok && result.success) {
        // Socket notification
        if (socket && selectedProviderData) {
          // Build procedure summary for notification
          const procedureSummary = validProcedures.map(proc => {
            const procDetail = procedures.find(p => p.procedureId === proc.procedureId);
            return {
              procedureName: procDetail?.procedureName || 'Unknown',
              quantity: proc.quantity,
              price: proc.price,
              totalPrice: proc.totalPrice
            };
          });

          // ✅ Build the message — omit the total when user has no pricing access
          const messageTotalSuffix = hasPricingAccess
            ? ` Total: ${getCurrencySymbol()}${grandTotal.toLocaleString()}`
            : '';

          const notificationData = {
            _id: `notif_${Date.now()}`,
            recipientLabId: selectedProviderData.userLabId || selectedProviderData.labId,
            type: isReOutsource ? 're_outsource_request' : 'outsource_request',
            title: isReOutsource ? 'New Re-Outsource Request' : 'New Outsource Request',
            message: `${labName} has sent you ${isReOutsource ? 'a re-outsource request' : 'an outsource request'} for case #${caseNumber || caseId} with ${validProcedures.length} procedure(s)${selectedFileIds.length > 0 ? ` and ${selectedFileIds.length} file(s)` : ''}.${messageTotalSuffix}`,
            data: {
              outsourceCaseId: result.data?._id,
              caseId: caseId,
              caseNumber: caseNumber || caseId,
              procedures: procedureSummary,
              totalPrice: grandTotal,
              totalQuantity: totalQty,
              requiredDate: requiredDate,
              sourceLabName: labName,
              notes: notes,
              selectedFileCount: selectedFileIds.length,
              isReOutsource
            },
            sender: {
              labName: labName,
              labId: currentUserLabId
            },
            createdAt: new Date().toISOString()
          };

          socket.emit('outsource-request-created', notificationData);
          console.log('📨 Socket notification sent to lab:', selectedProviderData.labName);
        }

        // ✅ Build success message — hide total when no pricing access
        let successMessage = `Outsource case created successfully with ${validProcedures.length} procedure(s)!`;
        if (hasPricingAccess && totalQty > 1) {
          successMessage += ` Total: ${getCurrencySymbol()}${grandTotal.toLocaleString()}`;
        }
        if (selectedFileIds.length > 0) {
          successMessage += ` Including ${selectedFileIds.length} file(s).`;
        }
        successMessage += ' Notification sent to lab.';

        setSuccess(successMessage);

        setTimeout(() => {
          handleClose();
          resetForm();
        }, 2000);
      } else {
        setError(result.message || "Failed to create outsource case");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('❌ Error creating outsource case:', error);
      setError("Network error. Please try again.");
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const selectedProviderData = providers.find(p => p._id === selectedProvider);

  const totalReceivedFiles = receivedStlFiles.length + receivedImageFiles.length + receivedOtherFiles.length;
  const totalUploadedFiles = uploadedStlFiles.length + uploadedImageFiles.length + uploadedOtherFiles.length;
  const totalFiles = isOutsourceCasePage ?
    (totalReceivedFiles + totalUploadedFiles) :
    (stlFiles.length + imageFiles.length + otherFiles.length);

  const currentUserLabId = getCurrentUserLabId();
  const grandTotal = calculateGrandTotal();
  const totalQuantity = calculateTotalQuantity();

  // Render file section with source label (for outsource-case page)
  const renderFileSectionWithSource = (title, files, icon, sourceLabel) => {
    if (!files || files.length === 0) return null;

    return (
      <div style={{ marginBottom: "20px" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h6 style={{ margin: 0, color: "#555" }}>
              {icon}
              {title} ({files.length})
            </h6>
            <span style={{
              fontSize: "11px",
              backgroundColor: sourceLabel === "Received" ? "#d4edda" : "#cce5ff",
              color: sourceLabel === "Received" ? "#155724" : "#004085",
              padding: "2px 8px",
              borderRadius: "10px",
              fontWeight: "500"
            }}>
              {sourceLabel === "Received" ? "📥 Received" : "📤 Uploaded"}
            </span>
          </div>
          <div>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleSelectAllFiles(files, true)}
              style={{ fontSize: "12px", padding: "0 5px" }}
            >
              Select All
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleSelectAllFiles(files, false)}
              style={{ fontSize: "12px", padding: "0 5px", color: "#dc3545" }}
            >
              Deselect All
            </Button>
          </div>
        </div>
        <div style={{
          maxHeight: "200px",
          overflowY: "auto",
          border: "1px solid #eee",
          borderRadius: "4px"
        }}>
          {files.map((file) => (
            <FileItem
              key={file._id}
              file={file}
              isSelected={selectedFileIds.includes(file._id)}
              onSelect={handleFileSelect}
              onDownload={handleFileDownload}
              getFileUrl={getFileUrl}
              sourceLabel={sourceLabel}
            />
          ))}
        </div>
      </div>
    );
  };

  // Render simple file section (for case page)
  const renderSimpleFileSection = (title, files, icon) => {
    if (!files || files.length === 0) return null;

    return (
      <div style={{ marginBottom: "20px" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px"
        }}>
          <h6 style={{ margin: 0, color: "#555" }}>
            {icon}
            {title} ({files.length})
          </h6>
          <div>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleSelectAllFiles(files, true)}
              style={{ fontSize: "12px", padding: "0 5px" }}
            >
              Select All
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleSelectAllFiles(files, false)}
              style={{ fontSize: "12px", padding: "0 5px", color: "#dc3545" }}
            >
              Deselect All
            </Button>
          </div>
        </div>
        <div style={{
          maxHeight: "200px",
          overflowY: "auto",
          border: "1px solid #eee",
          borderRadius: "4px"
        }}>
          {files.map((file) => (
            <FileItem
              key={file._id}
              file={file}
              isSelected={selectedFileIds.includes(file._id)}
              onSelect={handleFileSelect}
              onDownload={handleFileDownload}
              getFileUrl={getFileUrl}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
    >
      <Modal.Header
        closeButton
        style={{
          backgroundColor: "#0d47a1",
          color: "#fff",
          borderBottom: "none",
          padding: "12px 20px",
        }}
      >
        <Modal.Title
          style={{
            fontWeight: "800",
            fontSize: "18px",
            width: "100%",
            textAlign: "center"
          }}
        >
          {isOutsourceCasePage ? 'Re-Outsource Case' : 'Add Outsource'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          backgroundColor: "#f8f9fa",
          padding: "25px",
          maxHeight: "70vh",
          overflowY: "auto"
        }}
      >
        {/* Case Info */}
        <div style={{
          backgroundColor: "#e3f2fd",
          padding: "10px 15px",
          borderRadius: "4px",
          marginBottom: "20px",
          fontSize: "14px"
        }}>
          <strong>Case Number:</strong> {caseNumber || 'N/A'}
          {currentUserLabId && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              <strong>Your Lab ID:</strong> {currentUserLabId}
            </div>
          )}
          {isOutsourceCasePage && sourceOutsourceCaseId && (
            <div style={{ fontSize: '12px', color: '#00695c', marginTop: '5px' }}>
              <strong>Mode:</strong> Re-Outsource (this case was outsourced to your lab)
            </div>
          )}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess("")} dismissible>
            {success}
          </Alert>
        )}

        <div style={{
          backgroundColor: "#fff",
          padding: "25px",
          borderRadius: "4px",
          border: "1px solid #dee2e6"
        }}>
          <h5 style={{ marginBottom: "25px", color: "#333", fontWeight: "600" }}>
            Outsource Details
          </h5>

          {/* Select Lab */}
          <Form.Group style={{ marginBottom: "20px" }}>
            <Form.Label style={{ fontWeight: "500" }}>
              Select Lab <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Select
              value={selectedProvider}
              onChange={handleProviderChange}
              disabled={loading}
              style={{ height: "40px" }}
            >
              <option value="">{loading ? "Loading labs..." : "Select a lab"}</option>
              {providers.map((provider) => (
                <option key={provider._id} value={provider._id}>
                  {provider.labName} - {provider.procedureCount || 0} shared procedure(s)
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* CHANGED: Multiple Procedures Section */}
          {selectedProvider && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "15px"
              }}>
                <Form.Label style={{ fontWeight: "500", margin: 0 }}>
                  Procedures <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addProcedureRow}
                  disabled={loadingProcedures || procedures.length === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "13px"
                  }}
                >
                  <FaPlus size={12} /> Add Procedure
                </Button>
              </div>

              {selectedProceduresList.map((procRow, index) => {
                const selectedProcedureData = procedures.find(p => p.procedureId === procRow.procedureId);
                const alreadySelectedIds = getSelectedProcedureIds(procRow.id);

                return (
                  <div
                    key={procRow.id}
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      marginBottom: "12px",
                      border: "1px solid #dee2e6",
                      position: "relative"
                    }}
                  >
                    {/* Procedure Number Badge */}
                    <div style={{
                      position: "absolute",
                      top: "-10px",
                      left: "15px",
                      backgroundColor: "#0d47a1",
                      color: "white",
                      padding: "2px 12px",
                      borderRadius: "10px",
                      fontSize: "12px",
                      fontWeight: "600"
                    }}>
                      Procedure {index + 1}
                    </div>

                    {/* Remove Button */}
                    {selectedProceduresList.length > 1 && (
                      <Button
                        variant="link"
                        onClick={() => removeProcedureRow(procRow.id)}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "10px",
                          color: "#dc3545",
                          padding: "5px",
                          fontSize: "16px"
                        }}
                        title="Remove procedure"
                      >
                        <FaTrash />
                      </Button>
                    )}

                    {/* Select Procedure */}
                    <Form.Group style={{ marginTop: "5px", marginBottom: "12px" }}>
                      <Form.Label style={{ fontSize: "13px", fontWeight: "500" }}>
                        Select Procedure
                      </Form.Label>
                      <Form.Select
                        value={procRow.procedureId}
                        onChange={(e) => handleProcedureChangeForRow(procRow.id, e.target.value)}
                        disabled={loadingProcedures}
                        style={{ height: "38px", fontSize: "13px" }}
                      >
                        <option value="">
                          {loadingProcedures ? "Loading..." : "Select a procedure"}
                        </option>
                        {procedures
                          .filter(p => !alreadySelectedIds.includes(p.procedureId))
                          .map((procedure) => (
                            <option key={procedure.procedureId} value={procedure.procedureId}>
                              {procedure.procedureName}
                              {hasPricingAccess ? ` - ${getCurrencySymbol()}${procedure.outsourcePrice}` : ''}
                            </option>
                          ))
                        }
                      </Form.Select>
                    </Form.Group>

                    {/* Procedure Details & Quantity */}
                    {selectedProcedureData && (
                      <div>
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "10px",
                          marginBottom: "12px",
                          fontSize: "12px",
                          color: "#555"
                        }}>
                          <div>
                            <strong>Category:</strong> {selectedProcedureData.category || 'N/A'}
                          </div>
                          <div>
                            <strong>Pricing For:</strong> {selectedProcedureData.pricingFor || 'N/A'}
                          </div>
                        </div>

                        {/* Quantity — always shown */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          flexWrap: "wrap"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Form.Label style={{ margin: 0, fontSize: "13px", fontWeight: "500" }}>
                              Qty:
                            </Form.Label>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              border: "1px solid #dee2e6",
                              borderRadius: "4px",
                              overflow: "hidden"
                            }}>
                              <Button
                                variant="light"
                                onClick={() => handleQuantityChangeForRow(procRow.id, procRow.quantity - 1)}
                                disabled={procRow.quantity <= 1}
                                style={{
                                  border: "none",
                                  borderRadius: "0",
                                  padding: "4px 12px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: "#0d47a1"
                                }}
                              >
                                −
                              </Button>

                              <Form.Control
                                type="number"
                                value={procRow.quantity}
                                onChange={(e) => handleQuantityChangeForRow(procRow.id, e.target.value)}
                                min="1"
                                max="100"
                                style={{
                                  width: "60px",
                                  textAlign: "center",
                                  border: "none",
                                  borderLeft: "1px solid #dee2e6",
                                  borderRight: "1px solid #dee2e6",
                                  borderRadius: "0",
                                  fontSize: "14px",
                                  fontWeight: "600"
                                }}
                              />

                              <Button
                                variant="light"
                                onClick={() => handleQuantityChangeForRow(procRow.id, procRow.quantity + 1)}
                                disabled={procRow.quantity >= 100}
                                style={{
                                  border: "none",
                                  borderRadius: "0",
                                  padding: "4px 12px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: "#0d47a1"
                                }}
                              >
                                +
                              </Button>
                            </div>
                          </div>

                          {/* ✅ Price breakdown — only when hasPricingAccess */}
                          {hasPricingAccess && (
                            <div style={{
                              backgroundColor: "#e3f2fd",
                              padding: "6px 15px",
                              borderRadius: "4px",
                              fontSize: "13px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px"
                            }}>
                              <span style={{ color: "#666" }}>
                                Unit: {getCurrencySymbol()}{procRow.price.toLocaleString()}
                              </span>
                              <span style={{ color: "#666" }}>×</span>
                              <span style={{ color: "#666" }}>{procRow.quantity}</span>
                              <span style={{ color: "#666" }}>=</span>
                              <span style={{
                                fontWeight: "bold",
                                color: "#0d47a1",
                                fontSize: "14px"
                              }}>
                                {getCurrencySymbol()}{procRow.totalPrice.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ✅ Grand Total summary — only when hasPricingAccess */}
              {hasPricingAccess && grandTotal > 0 && (
                <div style={{
                  backgroundColor: "#e8f5e9",
                  padding: "15px",
                  borderRadius: "8px",
                  border: "2px solid #4caf50",
                  marginTop: "10px"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px"
                  }}>
                    <div>
                      <div style={{ fontSize: "13px", color: "#2e7d32" }}>
                        {selectedProceduresList.filter(p => p.procedureId).length} Procedure(s)
                      </div>
                      <div style={{ fontSize: "13px", color: "#2e7d32" }}>
                        Total Quantity: {totalQuantity}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "13px", color: "#2e7d32" }}>
                        Subtotal: {getCurrencySymbol()}{grandTotal.toLocaleString()}
                      </div>
                      <div style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "#1b5e20"
                      }}>
                        Grand Total: {getCurrencySymbol()}{grandTotal.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* When no pricing access: still show a summary of procedure count & qty */}
              {!hasPricingAccess && selectedProceduresList.filter(p => p.procedureId).length > 0 && (
                <div style={{
                  backgroundColor: "#e8f5e9",
                  padding: "12px 15px",
                  borderRadius: "8px",
                  border: "2px solid #4caf50",
                  marginTop: "10px",
                  fontSize: "13px",
                  color: "#2e7d32"
                }}>
                  <strong>{selectedProceduresList.filter(p => p.procedureId).length}</strong> Procedure(s), Total Quantity: <strong>{totalQuantity}</strong>
                </div>
              )}
            </div>
          )}

          {/* Required Date - FIXED: min date set to today to allow same-day selection */}
          <Form.Group style={{ marginBottom: "20px" }}>
            <Form.Label style={{ fontWeight: "500" }}>
              Required Date <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="date"
              value={requiredDate}
              onChange={(e) => setRequiredDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              style={{ height: "40px" }}
            />
          </Form.Group>

          {/* Notes */}
          <Form.Group style={{ marginBottom: "20px" }}>
            <Form.Label style={{ fontWeight: "500" }}>
              Notes (Optional)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes or instructions..."
              style={{ resize: "vertical" }}
            />
          </Form.Group>

          {/* File Selection Section */}
          {totalFiles > 0 && (
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  padding: "10px",
                  backgroundColor: "#e9ecef",
                  borderRadius: "4px",
                  marginBottom: showFileSection ? "15px" : "0"
                }}
                onClick={() => setShowFileSection(!showFileSection)}
              >
                <span style={{ fontWeight: "600", color: "#333" }}>
                  📎 Case Files ({totalFiles} files available)
                  {selectedFileIds.length > 0 && (
                    <span style={{
                      marginLeft: "10px",
                      fontSize: "12px",
                      backgroundColor: "#0d47a1",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "12px"
                    }}>
                      {selectedFileIds.length} selected
                    </span>
                  )}
                </span>
                <span style={{ fontSize: "18px" }}>
                  {showFileSection ? "▼" : "▶"}
                </span>
              </div>

              {showFileSection && (
                <div style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  padding: "15px",
                  backgroundColor: "#fff"
                }}>
                  {loadingFiles ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Loading files...</span>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: "15px", fontSize: "14px", color: "#0d47a1" }}>
                        <strong>{selectedFileIds.length} file(s) selected for this outsource</strong>
                      </div>

                      {/* Show received/uploaded sections only on outsource-case page */}
                      {isOutsourceCasePage ? (
                        <>
                          {totalFiles > 0 && (
                            <>
                              {/* File Summary */}
                              <div style={{
                                display: "flex",
                                gap: "15px",
                                marginBottom: "15px",
                                padding: "10px",
                                backgroundColor: "#f8f9fa",
                                borderRadius: "4px",
                                fontSize: "12px",
                                flexWrap: "wrap"
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                  <span style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "#d4edda",
                                    borderRadius: "3px",
                                    display: "inline-block"
                                  }}></span>
                                  <span>📥 Received Files: {totalReceivedFiles}</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                  <span style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "#cce5ff",
                                    borderRadius: "3px",
                                    display: "inline-block"
                                  }}></span>
                                  <span>📤 Uploaded Files: {totalUploadedFiles}</span>
                                </div>
                              </div>

                              {/* Received Files Sections */}
                              {totalReceivedFiles > 0 && (
                                <div style={{
                                  marginBottom: "15px",
                                  padding: "10px",
                                  backgroundColor: "#f0fff4",
                                  borderRadius: "4px",
                                  borderLeft: "4px solid #28a745"
                                }}>
                                  <div style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#155724",
                                    marginBottom: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px"
                                  }}>
                                    <FaReceipt /> Received Files ({totalReceivedFiles})
                                  </div>

                                  {renderFileSectionWithSource(
                                    "Intraoral Scan Files",
                                    receivedStlFiles,
                                    <FaCube style={{ marginRight: "5px" }} />,
                                    "Received"
                                  )}

                                  {renderFileSectionWithSource(
                                    "Image Files",
                                    receivedImageFiles,
                                    <FaImage style={{ marginRight: "5px" }} />,
                                    "Received"
                                  )}

                                  {renderFileSectionWithSource(
                                    "Other Files",
                                    receivedOtherFiles,
                                    <FaFile style={{ marginRight: "5px" }} />,
                                    "Received"
                                  )}
                                </div>
                              )}

                              {/* Uploaded Files Sections */}
                              {totalUploadedFiles > 0 && (
                                <div style={{
                                  marginBottom: "15px",
                                  padding: "10px",
                                  backgroundColor: "#f0f8ff",
                                  borderRadius: "4px",
                                  borderLeft: "4px solid #007bff"
                                }}>
                                  <div style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#004085",
                                    marginBottom: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px"
                                  }}>
                                    <FaUpload /> Uploaded Files ({totalUploadedFiles})
                                  </div>

                                  {renderFileSectionWithSource(
                                    "Intraoral Scan Files",
                                    uploadedStlFiles,
                                    <FaCube style={{ marginRight: "5px" }} />,
                                    "Uploaded"
                                  )}

                                  {renderFileSectionWithSource(
                                    "Image Files",
                                    uploadedImageFiles,
                                    <FaImage style={{ marginRight: "5px" }} />,
                                    "Uploaded"
                                  )}

                                  {renderFileSectionWithSource(
                                    "Other Files",
                                    uploadedOtherFiles,
                                    <FaFile style={{ marginRight: "5px" }} />,
                                    "Uploaded"
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Simple file display for case page */}
                          {renderSimpleFileSection(
                            "Intraoral Scan Files",
                            stlFiles,
                            <FaCube style={{ marginRight: "5px" }} />
                          )}

                          {renderSimpleFileSection(
                            "Image Files",
                            imageFiles,
                            <FaImage style={{ marginRight: "5px" }} />
                          )}

                          {renderSimpleFileSection(
                            "Other Files",
                            otherFiles,
                            <FaFile style={{ marginRight: "5px" }} />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {totalFiles === 0 && !loadingFiles && (
            <div style={{
              marginTop: "20px",
              padding: "15px",
              backgroundColor: "#f8f9fa",
              borderRadius: "4px",
              textAlign: "center",
              color: "#6c757d"
            }}>
              No files available for this case.
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer
        style={{
          justifyContent: "flex-end",
          borderTop: "none",
          padding: "15px 25px",
          backgroundColor: "#f8f9fa"
        }}
      >
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={loading}
          style={{
            padding: "8px 20px",
            marginRight: "10px"
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSave}
          disabled={loading || isSubmitting || !selectedProvider || selectedProceduresList.filter(p => p.procedureId).length === 0 || !requiredDate}
          style={{
            backgroundColor: "#0d47a1",
            border: "none",
            padding: "8px 20px",
            minWidth: "100px"
          }}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                style={{ marginRight: "8px" }}
              />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// File Item Component (unchanged)
const FileItem = ({ file, isSelected, onSelect, onDownload, getFileUrl, sourceLabel }) => {
  const getFileIcon = () => {
    const fileType = file.fileType || '';

    if (fileType === 'stl') {
      return <FaCube style={{ fontSize: "18px", marginRight: "10px", color: "#0d47a1" }} />;
    }

    if (fileType === 'image') {
      return <FaImage style={{ fontSize: "18px", marginRight: "10px", color: "#28a745" }} />;
    }

    if (fileType === 'pdf') {
      return <FaFilePdf style={{ fontSize: "18px", marginRight: "10px", color: "#dc3545" }} />;
    }

    const fileName = (file.name || file.filePath || file.path || '').toLowerCase();
    if (fileName.endsWith('.pdf')) {
      return <FaFilePdf style={{ fontSize: "18px", marginRight: "10px", color: "#dc3545" }} />;
    }

    if (fileName.match(/\.(png|jpg|jpeg|gif|bmp|webp|svg|tiff?)$/)) {
      return <FaImage style={{ fontSize: "18px", marginRight: "10px", color: "#28a745" }} />;
    }

    return <FaFile style={{ fontSize: "18px", marginRight: "10px", color: "#6c757d" }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const fileName = file.name || file.originalName || file.fileName ||
                  (file.filePath ? file.filePath.split(/[\\/]/).pop() :
                  file.path ? file.path.split(/[\\/]/).pop() : 'Unnamed File');

  const fileSize = file.size || file.fileSize || 0;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      padding: "10px 12px",
      borderBottom: "1px solid #f0f0f0",
      backgroundColor: isSelected ? "#e3f2fd" : "transparent",
      transition: "background-color 0.2s",
      cursor: "pointer"
    }}
    onClick={() => onSelect(file._id)}
    >
      <Form.Check
        type="checkbox"
        checked={isSelected}
        onChange={() => onSelect(file._id)}
        style={{ marginRight: "12px" }}
        onClick={(e) => e.stopPropagation()}
      />

      {getFileIcon()}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: "500",
          fontSize: "13px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>
          {fileName}
        </div>
        <div style={{ fontSize: "11px", color: "#6c757d" }}>
          {formatFileSize(fileSize)} • {formatDate(file.date || file.uploadedAt || file.createdAt)}
          {sourceLabel && (
            <span style={{
              marginLeft: "8px",
              fontSize: "10px",
              backgroundColor: sourceLabel === "Received" ? "#d4edda" : "#cce5ff",
              color: sourceLabel === "Received" ? "#155724" : "#004085",
              padding: "1px 6px",
              borderRadius: "8px"
            }}>
              {sourceLabel}
            </span>
          )}
          {file.fileType && (
            <span style={{
              marginLeft: "8px",
              fontSize: "10px",
              backgroundColor: "#e9ecef",
              color: "#495057",
              padding: "1px 6px",
              borderRadius: "8px"
            }}>
              {file.fileType.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      <Button
        variant="link"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onDownload(file);
        }}
        style={{
          padding: "5px",
          color: "#0d47a1"
        }}
        title="Download file"
      >
        <FaDownload size={14} />
      </Button>
    </div>
  );
};

export default Outsource;